import {
  ChronologySortField,
  ChronologyViewType,
  SortDirection,
  type VisitDisplay,
  type VisitsFlatItem,
} from "../types";
import {
  normalizeGroupKey,
  sortChronologyGroupedEntries,
  sortVisitsForChronology,
} from "./visitSorting";

export function buildGroupedVisitEntries(
  processedVisits: VisitDisplay[],
  viewType: ChronologyViewType,
  fileMapping: Record<string, string>,
  chronologySortField: ChronologySortField,
  chronologySortDirection: SortDirection,
  formatVisitType: (visitTypeId: string) => string = (visitTypeId) => visitTypeId,
  preserveOrder = false
): [string, VisitDisplay[]][] {
  if (viewType === ChronologyViewType.PROVIDER) {
    const buckets = processedVisits.reduce<
      Record<string, { label: string; visits: VisitDisplay[] }>
    >((acc, v) => {
      const key = normalizeGroupKey(v.provider_name);
      const label = (v.provider_name ?? "").trim().replace(/\s+/g, " ");
      if (!acc[key]) {
        acc[key] = { label, visits: [] };
      } else if (label < acc[key].label) {
        // The label doubles as the group key that expansion state is stored under, so it
        // must not depend on visit order — the list is re-sorted by date on every save.
        // Code-unit comparison also prefers the capitalized variant over a lowercase one.
        acc[key].label = label;
      }
      acc[key].visits.push(v);
      return acc;
    }, {});
    const raw = Object.values(buckets).map(
      ({ label, visits }) => [label, visits] as [string, VisitDisplay[]]
    );
    return sortChronologyGroupedEntries(
      raw,
      chronologySortField,
      chronologySortDirection,
      (key) => key,
      preserveOrder
    );
  }
  if (viewType === ChronologyViewType.VISIT_TYPE) {
    const raw = Object.entries(
      processedVisits.reduce<Record<string, VisitDisplay[]>>((acc, v) => {
        const key = v.visit_type_id;
        if (!acc[key]) acc[key] = [];
        acc[key].push(v);
        return acc;
      }, {})
    );
    return sortChronologyGroupedEntries(
      raw,
      chronologySortField,
      chronologySortDirection,
      formatVisitType,
      preserveOrder
    );
  }
  if (viewType === ChronologyViewType.FILE) {
    const visitsByFileId = processedVisits.reduce<Record<string, VisitDisplay[]>>((acc, v) => {
      const key = v.citations[0]?.file_id ?? "unknown";
      if (!acc[key]) acc[key] = [];
      acc[key].push(v);
      return acc;
    }, {});
    for (const fileId of Object.keys(fileMapping)) {
      if (!visitsByFileId[fileId]) visitsByFileId[fileId] = [];
    }
    return sortChronologyGroupedEntries(
      Object.entries(visitsByFileId),
      chronologySortField,
      chronologySortDirection,
      (key) => fileMapping[key] || "Unknown File",
      preserveOrder
    );
  }
  return [
    [
      "All visits",
      sortVisitsForChronology(
        processedVisits,
        chronologySortField,
        chronologySortDirection,
        preserveOrder
      ),
    ],
  ];
}

export function getVisibleGroupedEntries(
  groupedEntries: [string, VisitDisplay[]][],
  viewType: ChronologyViewType
): [string, VisitDisplay[]][] {
  if (viewType === ChronologyViewType.FILE) return groupedEntries;
  return groupedEntries.filter(([, g]) => g.length > 0);
}

export function buildVisitsFlatItems(
  visibleGroupedEntries: [string, VisitDisplay[]][],
  expandedGroups: Record<string, boolean>,
  viewType: ChronologyViewType,
  fileMapping: Record<string, string>
): VisitsFlatItem[] {
  return visibleGroupedEntries.flatMap(([key, groupVisits]) => {
    const label = viewType === ChronologyViewType.FILE ? fileMapping[key] || "Unknown File" : key;
    const count = groupVisits.reduce((c, v) => c + (v.allVisits?.length ?? 1), 0);
    const isExpanded = expandedGroups[key] ?? viewType === ChronologyViewType.DATE;
    const header: VisitsFlatItem = {
      kind: "header",
      groupKey: key,
      groupLabel: label,
      visitCount: count,
    };
    const visitRows: VisitsFlatItem[] = isExpanded
      ? groupVisits.map((v) => ({ kind: "visit", visit: v }))
      : [];
    return [header, ...visitRows];
  });
}
